import { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Zap } from "lucide-react";
import { toast } from "sonner";

export const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error();
      toast.success("Message sent! Thanks for reaching out! 🎮", { duration: 3000 });
      setFormData({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send your message. Please try again.", { duration: 3000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 sm:py-24 lg:py-32 bg-[hsl(var(--background))]"
      data-testid="contact-section"
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-3xl">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="contact-title">
            Let&apos;s Connect
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))]" data-testid="contact-subtitle">
            Ready to collaborate? Drop me a message
          </p>
        </motion.div>

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
        >
          <Card className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="font-medium text-sm block mb-2 text-[hsl(var(--foreground))]" htmlFor="name">
                  Name
                </label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-all"
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="font-medium text-sm block mb-2 text-[hsl(var(--foreground))]" htmlFor="email">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-all"
                  required
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="font-medium text-sm block mb-2 text-[hsl(var(--foreground))]" htmlFor="message">
                  Message
                </label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell me about your project..."
                  className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] min-h-[150px] transition-all"
                  required
                  data-testid="contact-message-textarea"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="modern-button w-full rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 font-semibold text-base py-6 shadow-lg hover:shadow-xl glow"
                data-testid="contact-submit-button"
              >
                <Zap className="w-5 h-5 mr-2" />
                Send Message
              </Button>
            </form>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
